/**
 * Canonical `artifact.processingStatus` values + the readiness gate.
 *
 * The column is a free-form `String` (default `'ready'`), not a DB enum — these
 * are the values the application writes and branches on. Centralised here so
 * frontend, backend, and the agent service share ONE definition of "is this
 * artifact usable yet" rather than scattering string literals (the cross-package
 * SSOT-drift class — CR-003).
 *
 * Lifecycle for a resumable video upload:
 *   reserve → UPLOADING → (bytes land + verify) → TRANSCODING → READY | FAILED
 * Non-upload artifacts are created READY; OCR/extraction uses PENDING (the file
 * is already present + viewable).
 */
export const PROCESSING_STATUS = {
    /** Resumable upload in flight — bytes not yet landed (no storagePath). */
    UPLOADING: 'uploading',
    /** Bytes landed + verified; transcode worker running (not yet playable). */
    TRANSCODING: 'transcoding',
    /** OCR / extraction in progress — the file IS present and viewable. */
    PENDING: 'pending',
    /** Usable: bytes present, playable, derived assets ready. */
    READY: 'ready',
    /** Upload or transcode failed — never usable. */
    FAILED: 'failed',
};
/** Zod v3 requires a non-empty tuple rather than an enum-like object. Derive
 * that tuple from the canonical object so wire schemas cannot omit a status. */
export const PROCESSING_STATUS_VALUES = Object.values(PROCESSING_STATUS);
/**
 * Statuses hidden from every user-facing list / search / binding-candidate
 * reader: a row in one of these is NOT a usable attachment (in-flight upload,
 * mid-transcode, or failed). `'pending'` is deliberately NOT here — that file is
 * present and viewable while OCR runs.
 */
export const HIDDEN_PROCESSING_STATUSES = ['uploading', 'transcoding', 'failed'];
/**
 * Statuses hidden from the OWNER-FACING BROWSE surfaces — the artifact list, the
 * note/project attachment bar, the Capture Stream, and the attachment detail
 * page. Strictly narrower than `HIDDEN_PROCESSING_STATUSES`: `'transcoding'` and
 * `'failed'` are deliberately absent, so an upload that is mid-transcode renders
 * as a "Processing…" placeholder and one that could not be processed renders as
 * a failed tile — instead of either of them vanishing.
 *
 * That difference is the whole point. Hiding the row everywhere meant we told
 * the user "Video saved" and then showed them nothing for up to ~2.5 minutes
 * (prod p90 54s, max 152s) — including a 404 on the attachment's own URL, which
 * reads as "the upload failed". Browse surfaces show the work in progress;
 * every reader that would USE the file (search, bind candidates, agent
 * display_media / show_step_video, signed-URL issuance) keeps gating on
 * `isArtifactReady` / `HIDDEN_PROCESSING_STATUSES` and still sees nothing.
 *
 * `'uploading'` stays hidden — that row has no bytes at all yet (no
 * storagePath), so there is nothing to place.
 *
 * `'failed'` used to stay hidden too, on the argument that the client learns
 * about a failure from its own upload-status poll rather than from "a tombstone
 * row every workspace member would see forever". That argument stopped holding
 * when #1503 made a failed upload RETAIN its raw original: the poll is scoped to
 * a live upload session, so once the tab closes the retained bytes were
 * unreachable — invisible on every surface, unsignable by every reader, with no
 * retry and no way to delete them individually. The user was charged storage for
 * a file they could not see, fetch, or discard.
 *
 * A row you can download and delete is not a tombstone; it is an item of work.
 * Note the status covers two populations with opposite properties — the
 * transcode paths RETAIN the original, while `uploadOrphanSweep.prune()` and a
 * rejected finalize delete it — so a reader of a failed row must branch on
 * whether `content.storagePath` exists rather than assume bytes are there.
 * Making it visible is what turns that retention from a cost into the recovery
 * it was meant to be. It is workspace-visible rather than uploader-only
 * deliberately: a colleague looking at a note whose recording silently vanished
 * is worse served than one who is told it could not be processed, and a list
 * whose contents differ per viewer is a bug class of its own.
 */
export const BROWSE_HIDDEN_PROCESSING_STATUSES = ['uploading'];
/**
 * Statuses hidden from the failed-original RECOVERY surfaces — the two download
 * endpoints, and nothing else. Exactly `HIDDEN_PROCESSING_STATUSES` minus
 * `'failed'`, so the carve-out is expressed as a subtraction from the strict set
 * rather than as its own list that could drift from it.
 *
 * This is a NARROWER opt-in than `BROWSE_HIDDEN_PROCESSING_STATUSES`, not the
 * same one: a browse surface may show a mid-transcode row, and the recovery
 * surfaces must never resolve one. The raw pre-transcode original is the file
 * the transcode pipeline exists never to serve, and it is still on its way to
 * being superseded. A failed original never will be — it is all the user has.
 */
export const RECOVERY_HIDDEN_PROCESSING_STATUSES = HIDDEN_PROCESSING_STATUSES.filter((status) => status !== PROCESSING_STATUS.FAILED);
/**
 * The statuses a browse surface SHOWS but no general reader may sign a URL for —
 * derived as the set difference, never restated.
 *
 * This is the safety property the browse carve-out rests on, and it was
 * previously expressed twice: once as this difference (which decides what the
 * readers return) and once as a hardcoded `=== 'transcoding'` (which decided
 * what `enrichAttachmentUrls` refuses to sign). They agreed only because the
 * difference happened to be exactly one value. Adding a third browse-visible
 * status later — the obvious next use of this mechanism — would have made the
 * readers return a row that the sign-guard did not recognise, and the raw bytes
 * would have been signed. Silent, and in the capability-granting direction.
 *
 * `'failed'` is that third status, and this is the mechanism working: adding it
 * to the browse set above extended the sign refusal to it with no second edit.
 * The single deliberate exception is the recovery download — see
 * `RECOVERY_HIDDEN_PROCESSING_STATUSES`, which is an explicit opt-in on two
 * named endpoints and never a property of the row.
 */
export const BROWSE_VISIBLE_UNSIGNABLE_STATUSES = HIDDEN_PROCESSING_STATUSES.filter((status) => !BROWSE_HIDDEN_PROCESSING_STATUSES.includes(status));
/**
 * True when a browse surface may show the row but no URL may be signed for it —
 * today, mid-transcode or failed. THE sign guard: `enrichAttachmentUrls` and the
 * linked-source path resolver both gate on this, so a status added to
 * `BROWSE_VISIBLE_UNSIGNABLE_STATUSES` inherits the refusal by construction.
 *
 * Distinct from the two render-side predicates below on purpose. Those say WHICH
 * placeholder to draw, and are one status each; this one says whether bytes may
 * leave, and is the whole set. Collapsing them — as they were while the set had
 * one member — is how a new browse-visible status silently becomes signable.
 */
export function isArtifactUnsignable(artifact) {
    const status = artifact.processingStatus;
    return !!status && BROWSE_VISIBLE_UNSIGNABLE_STATUSES.includes(status);
}
/**
 * True while an artifact is browse-visible and still being converted. The
 * render-side companion to `BROWSE_HIDDEN_PROCESSING_STATUSES`: a reader that
 * opts into showing these rows MUST branch on this to render a "Processing…"
 * placeholder rather than hand a half-ready file to a player.
 *
 * Not the sign guard — use `isArtifactUnsignable` for that.
 */
export function isArtifactProcessing(artifact) {
    return artifact.processingStatus === PROCESSING_STATUS.TRANSCODING;
}
/**
 * True when processing finished and rejected the file. The row is browse-visible
 * so the user can see what happened, download the retained original, and delete
 * it — but it is never usable content: `isArtifactReady` stays false and no
 * general reader may sign a URL for it.
 */
export function isArtifactFailed(artifact) {
    return artifact.processingStatus === PROCESSING_STATUS.FAILED;
}
/**
 * The single readiness gate. Every reader that would surface a video for use —
 * lists, search, protocol bind candidates, the agent `display_media` /
 * `show_step_video` tools, the Viture media panel, signed-URL issuance — gates
 * on this instead of testing row existence. A half-uploaded or mid-transcode
 * video must never reach a player.
 *
 * Default-ready: legacy rows and non-upload artifacts carry no status (or
 * `'ready'`), so an absent status reads as ready.
 */
export function isArtifactReady(artifact) {
    const status = artifact.processingStatus ?? PROCESSING_STATUS.READY;
    return !HIDDEN_PROCESSING_STATUSES.includes(status);
}
//# sourceMappingURL=artifact-status.js.map